import { useState, useContext } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ProfileContext } from "../../../../store/ProfileProvider";
import DisplayAmountWithDecimals from "../../shared/DisplayAmountWithDecimals";
import { ImSpinner8 } from "react-icons/im";

dayjs.extend(utc);
dayjs.extend(timezone);

type Props = {
  tradeDetail: any;
};

const TicketDetailsAccordion: React.FC<Props> = ({ tradeDetail }) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [isOpen, setIsOpen] = useState(true);

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp)).tz(profileTimeZone).format("DD MMM, YYYY");
  };

  const displayStatus = (status: string) => {
    switch (status) {
      case "awaiting":
        return <span className="status-awaiting">Awaiting</span>;
      case "pending":
        return <span className="status-pending">Pending</span>;
      case "completed":
        return <span className="status-completed">Completed</span>;
      case "cancelled":
        return <span className="status-cancelled">Cancelled</span>;
      case "rejected":
        return <span className="status-rejected">Rejected</span>;
      default:
        return <span className="status-awaiting">{status}</span>;
    }
  };

  const formatAddress = (address?: string): string => {
    if (!address || address.length < 10) return 'N/A';
    return `${address.slice(0, 12)}...${address.slice(-6)}`;
  };

  return (
    <div className="card mb-4 !rounded-xl !border-0 p-4">
      <button
        className="flex w-full items-center justify-between text-sm font-bold text-gray-700"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-primary-500">Trade Details</span>
        <svg
          className={`h-4 w-4 transform transition-transform duration-200 ${isOpen ? "rotate-180" : ""
            }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          {tradeDetail &&
            <div className="mt-4 grid grid-cols-[auto,1fr] gap-x-2 gap-y-2 text-sm text-gray-700">
              <span>Created Date:</span>
              <span className="text-right font-medium text-navy-700">
                {formatDate(tradeDetail?.created_at)}
              </span>
              <span>Trade ID:</span>
              <span className="text-right font-medium text-navy-700">
                #{tradeDetail?.shortid}
              </span>
              {tradeDetail.from_network === "fiat" &&
                <>
                  <span>Address:</span>
                  <span className="text-right font-medium text-navy-700">
                    {formatAddress(tradeDetail?.to_address?.address)}
                  </span>
                </>
              }
              <span>Amount:</span>
              <span className="text-right font-medium text-navy-700">
                <DisplayAmountWithDecimals amount={Number(tradeDetail?.from_amount)} currency={tradeDetail?.from_asset} decimal={tradeDetail?.from_asset_decimal} />
              </span>
              <span>Asset:</span>
              <span className="text-right font-medium text-navy-700">{tradeDetail?.from_asset} {tradeDetail?.from_network && tradeDetail?.from_network !== "fiat" && `(${tradeDetail?.from_network})`}</span>
              {tradeDetail?.status === "completed" &&
                <>
                  <span>Ref.:</span>
                  <span className="text-right font-medium text-navy-700">{tradeDetail?.transaction_reference}</span>
                </>
              }
              {tradeDetail?.status === "rejected" &&
                <>
                  <span>Reason:</span>
                  <span className="text-right font-medium text-navy-700">{tradeDetail?.reason}</span>
                </>
              }
              <span>Trade Status:</span>
              <span className="text-right font-medium">{displayStatus(tradeDetail?.status)}</span>
            </div>
          }
          {!tradeDetail &&
            <div className="flex items-center justify-center h-full">
              <ImSpinner8 className="spinner text-primary-500" style={{ fontSize: '2.2rem' }} />
            </div>
          }
        </>
      )}
    </div>
  );
}

export default TicketDetailsAccordion;
